import * as React from 'react';
import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useSystem } from '../../../hooks/useSystem';
import { FormikContext, useFormik } from 'formik';
import { Routes } from '../../../routes';
import { OrgPageWidget } from '../../../widgets/page/OrgPageWidget';
import { PageHeadingWidget } from '../../../widgets/page/PageHeadingWidget';
import { FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { FormikInputWidget } from '../../../widgets/inputs/FormikInput';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { styled } from '../../../theme';
import { useOrgStore } from '../../../hooks/useOrgStore';

export const SettingsPage: React.FC = observer(() => {
  const system = useSystem();
  const orgStore = useOrgStore();
  const org = orgStore.current_org!;

  const formik = useFormik<{ name: string }>({
    initialValues: {
      name: ''
    },
    onSubmit: async ({ name }) => {
      if (!org) {
        return;
      }
      try {
        await system.accountV5Client.updateOrg({
          id: org.org_id,
          label: name
        });
        await org.reload();
        system.dialogStore.infoDialog({
          title: 'Success',
          message:
            'Organization details were updated. It may take some time before the changes are propagated across all applications.'
        });
      } catch (ex) {
        system.dialogStore.errorDialog({
          title: 'Oops, something went wrong',
          errorMessage: 'Failed to update organization details.'
        });
      } finally {
      }
    },
    validateOnBlur: false,
    validate: (values) => {
      if (values.name.trim() === '') {
        return {
          name: 'Name must be set'
        };
      }
    }
  });

  useEffect(() => {
    if (org) {
      formik.setValues({
        name: org.model.label
      });
    }
  }, [org]);

  return (
    <OrgPageWidget activeItemKey={Routes.SETTINGS}>
      <S.Container>
        <PageHeadingWidget title={'Organization Settings'} />
        <FormikContext.Provider value={formik}>
          <S.FormContainer>
            <FormLayoutWidget
              sections={[
                {
                  key: 'details',
                  elements: [
                    <>
                      <S.Input name="name" label="Organization name" />
                    </>
                  ]
                },
                {
                  key: 'submit',
                  elements: [
                    <GroupedButtons
                      btns={[
                        {
                          label: 'Update details',
                          disabled: false,
                          action: async (event, loading) => {
                            try {
                              loading(true);
                              await formik.submitForm();
                            } finally {
                              loading(false);
                            }
                          }
                        }
                      ]}
                    />
                  ]
                }
              ]}
            />
          </S.FormContainer>
        </FormikContext.Provider>
      </S.Container>
    </OrgPageWidget>
  );
});

namespace S {
  export const Container = styled.div`
    margin: auto;
    margin-bottom: 20px;
    width: 600px;
  `;

  export const FormContainer = styled.div`
    background: ${(p) => p.theme.forms.panelBackground};
    padding: 30px;
    border-radius: 10px;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  `;

  export const Input = styled(FormikInputWidget)`
    margin-bottom: 15px;

    &:last-of-type {
      margin-bottom: 0;
    }
  `;
}
